/*
 * Copyright (C) 2025-2026 The Klangmotor Authors (see AUTHORS.MD)
 * SPDX-License-Identifier: AGPL-3.0-or-later
 */

package io.peekandpoke.klang.audio_be.effects

import io.peekandpoke.klang.audio_be.utils.TWO_PI
import io.peekandpoke.klang.audio_be.utils.finiteOrZero
import io.peekandpoke.klang.audio_be.utils.flushState
import io.peekandpoke.klang.audio_be.utils.wrapPhase
import kotlin.math.PI
import kotlin.math.sin
import kotlin.math.tan

/**
 * Single per-channel phaser kernel — `stages`-count first-order allpass cascade with
 * sine-LFO-modulated breakpoint and feedback. Used by both Phaser surfaces:
 *   - [Phaser]: cylinder bus, 2× PhaserCore for stereo
 *   - `ignitor/IgnitorEffects.kt::PhaserIgnitor`: Ignitor DSL, mono, built with the voice, `ctx.sampleRate` bound at the first block
 *
 * **Topology** — bilinear 1st-order allpass per stage:
 *   - `α = (tan(π·f/fs) − 1) / (tan(π·f/fs) + 1)`
 *   - per-stage update: `y = α·x + s;  s = x − α·y`
 *   - cascade with feedback: `input + lastOutput · feedback → cascade → output → lastOutput`
 *
 * The LFO sweeps the breakpoint frequency through `[MIN_MOD_FREQ_HZ, MAX_MOD_FREQ_HZ]`
 * around `center`. The kernel is **mono** and stateful; instantiate one per channel.
 *
 * Wrappers own their own input/output mixing (additive vs. crossfade) and stereo
 * dispatch — this kernel just produces the wet sample.
 *
 * **Control-rate α** — `α` (and the LFO) are computed at block boundaries via
 * [prepareBlock]; per-sample [step] linearly interpolates α from block-start to
 * block-end. This removes one `sin` and one `tan` from the per-sample hot path
 * (collectively ~50–150 ns/sample on Kotlin/JS). For typical LFO rates (≤10 Hz)
 * and block sizes (≤512 samples) the per-sample α error vs. sample-accurate
 * recomputation is < 10⁻⁵ relative — inaudible. **Callers MUST call
 * [prepareBlock] before each block of [step] calls.** Because the LFO waveform
 * itself is only sampled at block boundaries, the sweep's effective Nyquist is
 * `sampleRate / (2 · blockFrames)` (~187 Hz at 48 kHz / 128) — above that the
 * LFO aliases at the block rate and the aliased sweep depends on the block
 * size. `rate` is deliberately unclamped (raw engine); the bound is named here
 * and at the user-facing doors instead.
 *
 * **Inlining**: [step] is `inline` so the per-sample filter math expands at the
 * call site. Without this, the previous (non-inline) method-call boundary cost
 * ~25% on JVM and ~60% on Kotlin/JS for the stereo cylinder-bus path (two
 * `step()` calls per sample). The internal state members are `internal` (not
 * `private`) so the inline body can access them at call sites within the
 * `audio_be` module — `PhaserCore` is itself `internal class`, so this exposure
 * does not leak across modules.
 */
internal class PhaserCore(
    internal val stages: Int,
    sampleRate: Int,
) {
    /** `1 / sampleRate`; [bindSampleRate] moves it. */
    internal var inverseSampleRate: Double = 1.0 / sampleRate
        private set
    internal val z1 = DoubleArray(stages)
    internal var lastOutput: Double = 0.0
    internal var lfoPhase: Double = 0.0

    /** Current bilinear allpass coefficient, linearly interpolated across the block. */
    internal var alpha: Double = 0.0

    /** Per-sample α increment for the current block, set by [prepareBlock]. */
    internal var alphaIncrement: Double = 0.0

    /** LFO frequency in Hz. Setter silently ignores non-finite values. */
    var rate: Double = 0.0
        set(value) {
            if (!value.isFinite()) return
            field = value
        }

    /** Center breakpoint frequency in Hz around which the LFO sweeps. */
    var center: Double = 1000.0
        set(value) {
            if (!value.isFinite()) return
            field = value
        }

    /** LFO sweep width in Hz. Effective range = `[center - sweep/2, center + sweep/2]` clamped to `[MIN_MOD_FREQ_HZ, MAX_MOD_FREQ_HZ]`. */
    var sweep: Double = 1000.0
        set(value) {
            if (!value.isFinite()) return
            field = value
        }

    /** Feedback amount, clamped to `[0, MAX_FEEDBACK]`. NaN/Inf silently ignored. */
    var feedback: Double = 0.5
        set(value) {
            if (!value.isFinite()) return
            field = value.coerceIn(0.0, MAX_FEEDBACK)
        }

    /**
     * Compute α at the current LFO position, advance the LFO by [blockFrames]
     * samples, compute α at the new position, and set up the per-sample
     * increment for [step]. **Must be called before processing each block.**
     *
     * `blockFrames = 0` is a no-op (alphaIncrement set to 0; α unchanged).
     */
    fun prepareBlock(blockFrames: Int) {
        prepareBlock(blockFrames = blockFrames, centerTo = center, sweepTo = sweep)
    }

    /**
     * The same, for a caller that MOVES [center] or [sweep] between blocks: α at the block's start
     * is computed from the breakpoint in force so far, α at its end from [centerTo] / [sweepTo],
     * and [step] interpolates between them. So a breakpoint change reaches the cascade as a ramp
     * over the block instead of a jump at its first sample.
     *
     * Why that matters (Katalyst 5c-9, measured): the allpass multiplies its INPUT by α
     * (`y = α·x + s`), so a step in α is a step in the output, the loudest click of the whole
     * stage (a `center` of 100 Hz to 18 kHz measured -9.7 dB above 8 kHz against a chord, on a
     * steady floor of -52). With [centerTo] and [sweepTo] equal to what the core already holds,
     * this is `alphaAt(lfoPhase)` either way, so a phaser whose breakpoint stands is bit-identical
     * to the one-argument form.
     *
     * `blockFrames = 0` is a no-op and adopts NOTHING: no sample is produced, so the breakpoint in
     * force must not move out from under the next block's start.
     */
    fun prepareBlock(blockFrames: Int, centerTo: Double, sweepTo: Double) {
        prepare(blockFrames = blockFrames, centerTo = centerTo, sweepTo = sweepTo)
    }

    /**
     * The same, the breakpoint read from [block] ([PhaserBlock.centerTo], [PhaserBlock.sweepTo]): the form
     * [Phaser.process] calls, once per core and block.
     *
     * **No double crosses the call, on purpose** (Katalyst knob expressions step 6, 2026-10-12): with a modulated
     * center or sweep the breakpoint is a new non-integral double every block, and on V8 a double handed to a call
     * that is not inlined is a heap number (`audio/ref/performance.md`). As two arguments, V8 left the call out of
     * `Phaser.process` and boxed them: `E-kat-mix` read 226.1 B per block against 196.3 (`--predictable`,
     * single-case; mutant M8 and run T of `docs/benchmarks/2026-10-12_183400_knob-step6-v8.md`), about two heap
     * numbers, under its line. The body is [prepare], Kotlin `inline`, so this form reads the holder in place. No test
     * can pin it; this KDoc is the guard.
     */
    fun prepareBlock(blockFrames: Int, block: PhaserBlock) {
        prepare(blockFrames = blockFrames, centerTo = block.centerTo, sweepTo = block.sweepTo)
    }

    /** The body of both [prepareBlock] forms with a breakpoint; inline, so neither passes a double on. */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun prepare(blockFrames: Int, centerTo: Double, sweepTo: Double) {
        if (blockFrames <= 0) {
            alphaIncrement = 0.0
            return
        }

        val alphaStart = alphaAt(lfoPhase)

        center = centerTo
        sweep = sweepTo

        val phaseAdvance = rate * TWO_PI * inverseSampleRate * blockFrames
        val newPhase = (lfoPhase + phaseAdvance).wrapPhase(TWO_PI)

        val alphaEnd = alphaAt(newPhase)

        alpha = alphaStart
        alphaIncrement = (alphaEnd - alphaStart) / blockFrames
        lfoPhase = newPhase
    }

    /**
     * The all-pass coefficient at LFO [phase]. Kotlin `inline`, on purpose (engine follow-up 1, 2026-10-11): it runs
     * twice per core per block inside [prepareBlock], and in the orbit phaser's `Phaser.process`, pinned to one core,
     * V8 left it out, so its phase and its result crossed the call as heap numbers, about five per block for the
     * stereo pair (`audio/ref/performance.md`). Inline, they never cross. No test can pin it; this KDoc is the guard.
     */
    @Suppress("NOTHING_TO_INLINE")
    private inline fun alphaAt(phase: Double): Double {
        val lfoValue = (sin(phase) + 1.0) * 0.5
        var modFreq = center + (lfoValue - 0.5) * sweep
        if (modFreq < MIN_MOD_FREQ_HZ) {
            modFreq = MIN_MOD_FREQ_HZ
        } else if (modFreq > MAX_MOD_FREQ_HZ) {
            modFreq = MAX_MOD_FREQ_HZ
        }
        val tanV = tan(PI * modFreq * inverseSampleRate)
        return (tanV - 1.0) / (tanV + 1.0)
    }

    /**
     * Process one input sample using the linearly-interpolated α set up by
     * [prepareBlock]. Advances α by [alphaIncrement] for the next sample.
     * Returns the **wet** sample — wrappers decide how to mix it with dry.
     *
     * `inline` is load-bearing — the body expands at the call site so the per-
     * sample math fuses with the caller's loop. See class KDoc.
     */
    @Suppress("NOTHING_TO_INLINE")
    internal inline fun step(x: Double): Double {
        // Snap NaN/Inf input to 0 — never poison the cascade or feedback state.
        val safeX = x.finiteOrZero()

        val a = alpha
        var signal = safeX + lastOutput * feedback
        for (s in 0 until stages) {
            val y = a * signal + z1[s]
            z1[s] = (signal - a * y).flushState()
            signal = y
        }
        lastOutput = signal.flushState()
        alpha = a + alphaIncrement
        return signal
    }

    /**
     * Zero the LFO phase — for ORBIT REUSE only ([reset] deliberately preserves it). A torn-down
     * orbit's phase is "blocks the orbit happened to stay active x rate", which depends on the
     * cleanup schedule (block size x allocated-cylinder count) — carrying it into the orbit's
     * next life would make the reused orbit's sweep position framing-dependent (review round 2).
     */
    fun zeroPhase() {
        lfoPhase = 0.0
    }

    /**
     * Sets the rate the kernel runs at, for a caller that builds the kernel before it knows the rate: the Ignitor
     * phaser builds it with the voice and binds the context's rate at the first block (tidy-up step 10). Nothing
     * else changes; the same rate gives the same `1 / sampleRate` bit for bit.
     */
    fun bindSampleRate(sampleRate: Int) {
        inverseSampleRate = 1.0 / sampleRate
    }

    /** Clear allpass state and `lastOutput`. LFO phase is preserved for cross-note continuity. */
    fun reset() {
        for (i in 0 until stages) {
            z1[i] = 0.0
        }
        lastOutput = 0.0
    }

    companion object {
        /** Canonical phaser stage count — 2 notches, "Phase 90" / "Small Stone" character. */
        const val DEFAULT_STAGES: Int = 4

        /** Maximum stable feedback amount before self-oscillation. */
        const val MAX_FEEDBACK: Double = 0.95

        /** Lower clamp for LFO-modulated breakpoint frequency. */
        internal const val MIN_MOD_FREQ_HZ: Double = 100.0

        /** Upper clamp for LFO-modulated breakpoint frequency. Stays well below Nyquist at typical sample rates. */
        internal const val MAX_MOD_FREQ_HZ: Double = 18000.0
    }
}
